/**
 * Executions history tab for the agentic network room.
 *
 *   - Lists task rows from `GET /networks/{id}/executions`, scoped to the
 *     current pool when the row carries a `poolId`. Older rows from the
 *     workbench's network-level execute (no poolId) are filtered out so
 *     the agentic view only shows pool runs.
 *   - Hot-refreshes when an `EXECUTION_*` AppSync envelope arrives in the
 *     room channel (we don't subscribe again here — the parent passes the
 *     same `messages` buffer it already maintains).
 *   - Surfaces a clickable row → opens the side-panel detail.
 *
 * Visual contract:
 *   - Status pill on the left, prompt preview the focal column, started /
 *     duration aligned right. Same density as the rest of the room.
 *   - In-flight rows show a live-ticking duration computed from `createdAt`
 *     (and optionally a finish event from the channel buffer when present).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, ChevronRight, ChevronLeft } from 'lucide-react';
import { listNetworkExecutions } from '@/services/networkExecutionsApi';
import { listPoolMembers } from '@stepflow/ui-shared/services/poolMembersApi.js';
import { formatRelativeTime } from '@/utils/formatDate';
import { durationBetween } from '@/utils/formatDuration';
import { AGENT_EVENTS, envelopeEventType } from '@/constants/agentEvents';
import { bucketStatus, statusPillClassName, EXECUTION_STATUS_BUCKETS, isLiveBucket } from './executionStatus.js';
import ExecutionDetailDrawer from './ExecutionDetailDrawer.jsx';
import Spinner from '@stepflow/ui-shared/components/Spinner.jsx';

/** Hash of envelopes that affect the executions list — used as effect key. */
function lifecycleKey(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return '0';
  let last = '';
  let count = 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const et = envelopeEventType(messages[i]);
    if (
      et === AGENT_EVENTS.EXECUTION_STARTED
      || et === AGENT_EVENTS.EXECUTION_COMPLETED
      || et === AGENT_EVENTS.EXECUTION_FAILED
      || et === AGENT_EVENTS.SFN_TRANSITION
    ) {
      if (!last) last = messages[i].eventId || messages[i].timestamp || '';
      count += 1;
    }
    if (count >= 8) break;
  }
  return `${count}:${last}`;
}

/**
 * Latest terminal Step Functions state for this run from live envelopes.
 * When Dynamo is patched late or missing env, the table still shows RUNNING; this
 * aligns the pill + duration with EXECUTION_* (same source as the bridge).
 *
 * @returns {{ status: string, finishedAt: string|undefined }|null}
 */
function TerminalFromChannel(executionId, taskId, messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const want = new Set(
    [executionId, taskId, (executionId && String(executionId).split(':').pop())]
      .filter(Boolean)
      .map((x) => String(x)),
  );
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const env = messages[i];
    const et = envelopeEventType(env);
    if (et !== AGENT_EVENTS.EXECUTION_COMPLETED && et !== AGENT_EVENTS.EXECUTION_FAILED) continue;
    const p = env?.payload || {};
    const ex = String(
      p.executionId
      || env?.executionId
      || p.correlationId
      || env?.correlationId
      || '',
    );
    if (!want.has(ex)) continue;
    const st = (p.status || (et === AGENT_EVENTS.EXECUTION_COMPLETED ? 'SUCCEEDED' : 'FAILED')).toUpperCase();
    const finishedAt = env.timestamp || p.stopDate || p.endedAt;
    return { status: st, finishedAt };
  }
  return null;
}

/**
 * Trigger cell — HOW a run started: UI (a person in the room), API (SDK /
 * direct call), or JOB (a schedule). Stamped as ``triggerType`` on the Tasks
 * row by ``_agentic_record_room_run_artifacts``. JOB rows also show the loop
 * episode index. Legacy rows without a type render a neutral dash.
 */
const TRIGGER_STYLE = {
  UI: 'bg-blue-50 text-blue-700 ring-blue-200',
  API: 'bg-violet-50 text-violet-700 ring-violet-200',
  JOB: 'bg-amber-50 text-amber-700 ring-amber-200',
};
function TriggerBadge({ type, episode }) {
  const t = String(type || '').trim().toUpperCase();
  if (!t) return <span className="text-slate-300">—</span>;
  const cls = TRIGGER_STYLE[t] || 'bg-slate-100 text-slate-600 ring-slate-200';
  const epNum = Number.isFinite(Number(episode)) && episode != null
    ? Number(episode) + 1
    : null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${cls}`}>
        {t}
      </span>
      {t === 'JOB' && epNum ? (
        <span className="text-[10px] text-slate-400" title={`Episode ${epNum}`}>ep {epNum}</span>
      ) : null}
    </span>
  );
}

/**
 * Engine badge — marks a GENERATIVE run (``engine === 'manifest'``): it executed on
 * the ONE SHARED engine Step Function that runs every generative room, rather than a
 * per-network SFN. The tooltip shows ``enginePath`` — the full room path
 * (``networkId/poolId[/release]``), the "alias" that disambiguates a shared-engine run.
 * Renders nothing for classic per-network-SFN rows (no visual noise on those).
 */
function EngineBadge({ engine, enginePath }) {
  if (String(engine || '').trim().toLowerCase() !== 'manifest') return null;
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset bg-emerald-50 text-emerald-700 ring-emerald-200"
      title={enginePath ? `Generative — shared engine · ${enginePath}` : 'Generative — shared engine'}
    >
      GEN
    </span>
  );
}

// Cognito subs look like ``14a8d418-20a1-7077-b0cd-…`` (UUID-shaped). Treat those as NOT a
// human-readable name so they route through the roster resolver instead of showing raw. Mirrors
// the landing RoomExecutionRow (same fix — the raw sub used to leak into the User column here).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function looksLikeSub(s) {
  return UUID_RE.test(String(s || '').trim());
}
// Shorten a bare sub for the last-ditch fallback (no email, not in roster).
function shortenSub(s) {
  const v = String(s || '').trim();
  if (v.length <= 12) return v;
  return `${v.slice(0, 6)}…${v.slice(-4)}`;
}

/**
 * User cell — WHO triggered the run. Prefer a HUMAN-READABLE explicit field (actorEmail, or a
 * username that isn't itself a sub); otherwise resolve the triggering sub against the pool roster
 * (``resolveUser``) → name/email; finally shorten a bare UUID so a stranger's run isn't a 36-char
 * blob. A UUID-shaped username/triggeredBy is NOT treated as a name — that was the bug (raw sub
 * leaked into the column). Same resolution the Rooms-landing executions use.
 */
function UserCell({ row, resolveUser }) {
  const r = row || {};
  const rawUsername = String(r.username || '').trim();
  const explicit = String(r.actorEmail || '').trim()
    || (rawUsername && !looksLikeSub(rawUsername) ? rawUsername : '');
  const sub = String(r.triggeredBy || '').trim()
    || (looksLikeSub(rawUsername) ? rawUsername : '');
  const resolved = !explicit && sub && resolveUser ? resolveUser(sub) : null;
  const who = explicit || resolved || (sub ? shortenSub(sub) : '');
  const title = explicit || (resolved ? `${resolved}${sub ? ` · ${sub}` : ''}` : sub);
  if (!who) return <span className="text-slate-300">—</span>;
  return (
    <span className="block truncate text-slate-600" title={title || undefined}>{who}</span>
  );
}

function PromptPreview({ value }) {
  if (!value) {
    return <span className="text-slate-400">(no prompt)</span>;
  }
  const trimmed = String(value).replace(/\s+/g, ' ').trim();
  if (trimmed.length <= 140) return <span className="text-slate-700">{trimmed}</span>;
  return (
    <span className="text-slate-700" title={trimmed}>
      {`${trimmed.slice(0, 139)}…`}
    </span>
  );
}

/**
 * "Task done" cell — surfaces what each builder run accomplished, as
 * read by the smart-session classifier (label) and the agent itself
 * (summary). Both fields are stamped on the Tasks DDB row by
 * ``stream_runner._stamp_task_row`` at end-of-run; legacy or pre-stamp
 * rows render an em-dash placeholder so the column never collapses.
 *
 * Sibling component to ``ui-workbench``'s ``TaskDoneCell``. Defined
 * locally (rather than imported cross-package) to avoid pulling
 * ui-workbench into the ui-agentic bundle for one tiny cell.
 */
function TaskDoneCell({ label, summary }) {
  const lbl = String(label || '').trim();
  const sum = String(summary || '').trim();
  if (!lbl && !sum) {
    return <span className="text-slate-300">—</span>;
  }
  const tooltip = [lbl, sum].filter(Boolean).join('\n');
  // ``min-w-0`` lets the truncate spans actually clip inside the
  // table cell (without it, flex children default to ``min-width:auto``
  // = content-size, so ``truncate`` does nothing and the cell grows).
  // The summary line wraps to ~2 lines instead of single-line truncate,
  // so the agent's actual outcome is readable without hovering.
  return (
    <div className="flex min-w-0 flex-col gap-0.5" title={tooltip}>
      {lbl ? (
        <span className="truncate font-medium text-slate-700">{lbl}</span>
      ) : null}
      {sum ? (
        <span className="line-clamp-2 text-[11px] leading-snug text-slate-500">
          {sum}
        </span>
      ) : null}
    </div>
  );
}

function StatusPill({ raw }) {
  const bucket = bucketStatus(raw);
  const meta = EXECUTION_STATUS_BUCKETS[bucket];
  return (
    <span className={statusPillClassName(bucket)}>
      {isLiveBucket(bucket) ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />
      ) : null}
      {meta.label}
    </span>
  );
}

/**
 * @param {Object} props
 * @param {string} props.networkId
 * @param {string} props.poolId
 * @param {Array<Record<string, unknown>>} [props.messages]   Live AppSync buffer
 *   (already maintained by the parent room). Used only as a refresh trigger
 *   when EXECUTION_* events flow through.
 */
const PAGE_SIZE = 10;

export default function ExecutionsTab({ networkId, poolId, messages }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  // Pool roster (sub → display name/email) so the User column shows "Jane Doe", not a raw Cognito
  // UUID. Fetched once per room; the same map the Rooms-landing executions build. Best-effort — a
  // failed members fetch leaves the resolver empty and the cell falls back to a shortened sub.
  const [usersBySub, setUsersBySub] = useState({});
  useEffect(() => {
    if (!networkId || !poolId) { setUsersBySub({}); return undefined; }
    let cancelled = false;
    listPoolMembers(networkId, poolId)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const mem of (res?.members || [])) {
          const sub = String(mem?.userSub || '').trim();
          if (!sub) continue;
          // ``userName`` = the person's workspace name (JWT claims); ``email`` is the fallback.
          // ``displayName`` on a membership is the ROOM name, not the user — deliberately unused.
          const name = String(mem?.userName || '').trim();
          const email = String(mem?.email || '').trim();
          if (name || email) map[sub] = name || email;
        }
        setUsersBySub(map);
      })
      .catch(() => { if (!cancelled) setUsersBySub({}); });
    return () => { cancelled = true; };
  }, [networkId, poolId]);
  const resolveUser = useCallback((sub) => usersBySub[String(sub || '').trim()] || null, [usersBySub]);

  // Pagination state.
  //
  //   currentCursor   — the cursor used to fetch the page currently
  //                     on screen (``null`` for the first page).
  //   nextCursor      — cursor returned with the page; used by ``>``.
  //   cursorStack     — back-button history. Each ``>`` pushes the
  //                     prior ``currentCursor`` onto the stack;
  //                     ``<`` pops one off and refetches with it.
  //                     DDB queries are forward-only, so we maintain
  //                     this client-side rather than ask the backend
  //                     for reverse pagination.
  //   pageIndex       — purely cosmetic, shown in the header.
  const [currentCursor, setCurrentCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);

  // Date-range filter (ISO ``YYYY-MM-DD`` strings; empty = unset).
  // Native ``<input type="date">`` produces this format directly.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const lastFetchAt = useRef(0);

  const fetchPage = useCallback(
    async ({ cursor = null, silent = false, fromDate, toDate } = {}) => {
      if (!networkId || !poolId) return null;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const fromArg = (typeof fromDate === 'string' ? fromDate : dateFrom) || undefined;
        const toArg = (typeof toDate === 'string' ? toDate : dateTo) || undefined;
        const resp = await listNetworkExecutions(networkId, {
          poolId,
          limit: PAGE_SIZE,
          cursor: cursor || undefined,
          from: fromArg,
          // Inclusive upper bound: the GSI sort key is the ISO
          // ``createdAt`` (date+time). To make a date-only ``to``
          // include the entire day, append the end-of-day suffix.
          to: toArg ? `${toArg}T23:59:59.999Z` : undefined,
        });
        setRows(resp.executions);
        setNextCursor(resp.nextCursor || null);
        return resp;
      } catch (e) {
        setError(e.message || 'Failed to load executions');
        return null;
      } finally {
        setLoading(false);
        lastFetchAt.current = Date.now();
      }
    },
    [networkId, poolId, dateFrom, dateTo],
  );

  // Re-fetch the current page (no cursor changes). Used by the
  // refresh button + by live EXECUTION_* envelope refreshes.
  const refresh = useCallback(
    ({ silent = false } = {}) => fetchPage({ cursor: currentCursor, silent }),
    [fetchPage, currentCursor],
  );

  const goNext = useCallback(async () => {
    if (!nextCursor) return;
    const prev = currentCursor;
    setCursorStack((s) => [...s, prev]);
    setCurrentCursor(nextCursor);
    setPageIndex((n) => n + 1);
    await fetchPage({ cursor: nextCursor });
  }, [nextCursor, currentCursor, fetchPage]);

  const goPrev = useCallback(async () => {
    if (cursorStack.length === 0) return;
    const popped = cursorStack[cursorStack.length - 1];
    setCursorStack((s) => s.slice(0, -1));
    setCurrentCursor(popped);
    setPageIndex((n) => Math.max(1, n - 1));
    await fetchPage({ cursor: popped });
  }, [cursorStack, fetchPage]);

  // Initial + identity-change fetch (resets pagination).
  useEffect(() => {
    setCursorStack([]);
    setCurrentCursor(null);
    setPageIndex(1);
    fetchPage({ cursor: null });
    // ``fetchPage`` covers ``networkId``/``poolId``/``dateFrom``/``dateTo``;
    // we only want this to fire on identity / filter change, not on every
    // ``fetchPage`` re-instantiation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, poolId, dateFrom, dateTo]);

  // Live refresh on lifecycle events. Only when the user is on
  // page 1 with no date filter — otherwise an EXECUTION_STARTED
  // would yank them out of their browse. Throttled to 2s so bursts
  // of LOG_BATCH-co-emitted EXECUTION_* don't hammer the API.
  const onFirstUnfilteredPage = currentCursor === null && !dateFrom && !dateTo;
  const liveKey = lifecycleKey(messages);
  useEffect(() => {
    if (!liveKey || liveKey === '0') return undefined;
    if (!onFirstUnfilteredPage) return undefined;
    const elapsed = Date.now() - lastFetchAt.current;
    const delay = elapsed > 2000 ? 0 : 2000 - elapsed;
    const timer = window.setTimeout(() => {
      fetchPage({ cursor: null, silent: true });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [liveKey, onFirstUnfilteredPage, fetchPage]);

  // Re-tick the duration column for in-flight rows.
  const [, setTick] = useState(0);
  const hasLiveRow = useMemo(
    () => rows.some((r) => {
      const t = TerminalFromChannel(r.executionId, r.taskId, messages);
      return isLiveBucket(bucketStatus(t?.status || r.status));
    }),
    [rows, messages],
  );
  useEffect(() => {
    if (!hasLiveRow) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [hasLiveRow]);

  const visibleRows = rows;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          <span>Recent runs</span>
          {visibleRows.length ? (
            <span className="rounded bg-slate-200/60 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
              page {pageIndex} · {visibleRows.length}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Date range filter — uses native <input type=date> so we
              get keyboard input + the OS date picker for free. Empty
              inputs mean "no filter on that bound". Server-side
              filter via the ``createdAt`` GSI sort key. */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            disabled={loading}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 transition-colors hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Filter from date"
          />
          <span className="text-[11px] text-slate-500">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            disabled={loading}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 transition-colors hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Filter to date"
          />
          {(dateFrom || dateTo) ? (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              disabled={loading}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Clear
            </button>
          ) : null}
          {/* Page navigation. ``<`` pops the cursor stack; ``>`` pushes
              the current cursor and follows ``nextCursor``. Both
              disabled at boundaries so users can't get into a stuck
              state. */}
          <button
            type="button"
            onClick={goPrev}
            disabled={loading || cursorStack.length === 0}
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={loading || !nextCursor}
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => refresh()}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-3 w-3" aria-hidden />
            )}
            Refresh
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <div className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </div>
        ) : null}

        {/* While the first/refresh fetch is in flight with nothing to show yet,
            spin — don't flash the "No previous runs" empty-state. */}
        {loading && !error && visibleRows.length === 0 ? (
          <Spinner variant="panel" label="Loading executions…" />
        ) : null}

        {!loading && !error && visibleRows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No previous runs</p>
            <p className="mt-1 max-w-md text-xs text-slate-500">
              When you (or a teammate) executes a prompt in this room, the run will appear here with
              its status, duration, and full transcript.
            </p>
          </div>
        ) : null}

        {/*
         * Layout note: ``table-fixed`` would force a single absolute
         * width on each column. We had ``Prompt`` as the only flex
         * column (no <col> width) which made it absorb all leftover
         * space and squeezed ``Task done`` to a 200-px stub. Auto
         * layout (default) lets Prompt and Task done split the flex
         * budget proportionally to content while the four small
         * columns hold their min widths via the ``<col>`` styles
         * below. ``Task done`` is the more useful column once a run
         * completes — give it the larger share.
         */}
        {visibleRows.length > 0 ? (
          <table className="w-full text-left text-xs">
            <colgroup>
              <col style={{ width: '120px' }} />
              <col style={{ width: '76px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '40%' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '32px' }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-white/95 text-[10px] font-semibold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgba(15,23,42,0.06)]">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="py-2">Trigger</th>
                <th className="py-2">User</th>
                <th className="py-2">Prompt</th>
                <th className="py-2">Task done</th>
                <th className="px-3 py-2 text-right">Started</th>
                <th className="px-3 py-2 text-right">Duration</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRows.map((row) => {
                const channelTerminal = TerminalFromChannel(row.executionId, row.taskId, messages);
                const effectiveStatus = channelTerminal?.status || row.status;
                const bucket = bucketStatus(effectiveStatus);
                const live = isLiveBucket(bucket);
                const finishedAt = !live
                  ? (row.endedAt || row.updatedAt || row.completedAt
                    || channelTerminal?.finishedAt)
                  : undefined;
                const duration = durationBetween(row.createdAt, finishedAt);
                return (
                  <tr
                    key={row.taskId}
                    onClick={() => setSelectedTaskId(row.taskId)}
                    className={`group cursor-pointer transition-colors ${
                      selectedTaskId === row.taskId
                        ? 'bg-blue-50/60'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="px-4 py-2">
                      <StatusPill raw={effectiveStatus} />
                    </td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1">
                        <TriggerBadge type={row.triggerType} episode={row.loopIteration} />
                        <EngineBadge engine={row.engine} enginePath={row.enginePath} />
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <UserCell row={row} resolveUser={resolveUser} />
                    </td>
                    <td className="py-2 pr-3">
                      <PromptPreview value={row.prompt} />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <TaskDoneCell label={row.taskLabel} summary={row.taskSummary} />
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">
                      {formatRelativeTime(row.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">
                      {duration}
                    </td>
                    <td className="pr-3 text-slate-300 group-hover:text-slate-500">
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>

      <ExecutionDetailDrawer
        open={!!selectedTaskId}
        networkId={networkId}
        poolId={poolId}
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
      />
    </div>
  );
}
